<template>
  <div v-if="production" class="prod-stack">
    <form class="move-card prod-panel space-y-4" @submit.prevent="saveBody">
      <ProductionSectionHead
        title="Script"
        :subtitle="wordCount ? `${wordCount.toLocaleString()} words` : 'Write the draft here, or attach a file below.'"
      />
      <div class="move-field !mb-0">
        <textarea id="script-body" v-model="scriptBody" rows="16" class="move-textarea font-mono text-sm min-h-[22rem]" placeholder="INT. MARKET — DAY" aria-label="Script" />
      </div>
      <p v-if="message" class="prod-success">{{ message }}</p>
      <button type="submit" class="move-btn-primary">Save script</button>
    </form>

    <div class="move-card prod-panel space-y-4">
      <ProductionSectionHead title="Script file" subtitle="PDF or Word attachment for the locked draft." />
      <div v-if="production.script_file_url" class="prod-person-row">
        <span class="prod-role-icon">
          <span class="material-icons">attach_file</span>
        </span>
        <div class="min-w-0 flex-1">
          <a :href="production.script_file_url" target="_blank" rel="noopener" class="font-semibold" style="color: var(--move-accent)">
            {{ production.script_file_name || 'Download' }}
          </a>
          <p class="text-xs move-text-faint">Attached file</p>
        </div>
        <button type="button" class="move-icon-btn move-icon-btn--danger" aria-label="Remove file" @click="askRemoveFile">
          <span class="material-icons">close</span>
        </button>
      </div>
      <label
        class="prod-file-drop"
        :class="{ 'is-over': fileOver }"
        @dragover.prevent="fileOver = true"
        @dragleave="fileOver = false"
        @drop.prevent="onDrop"
      >
        <span class="font-semibold move-text">Drop a file or click to upload</span>
        <span class="text-sm">PDF, Word, or plain text</span>
        <input type="file" accept=".pdf,.doc,.docx,.txt,.rtf" @change="onFile" />
      </label>
      <p v-if="fileError" class="move-alert-error">{{ fileError }}</p>
    </div>

    <MoveConfirmDialog
      v-model="showRemoveModal"
      title="Remove script file"
      subtitle="The attached draft will be deleted."
      :detail-title="production.script_file_name || 'Attached file'"
      detail-tone="danger"
      confirm-label="Remove file"
      confirm-icon="delete"
      busy-label="Removing…"
      :busy="removing"
      :error="removeError"
      @confirm="confirmRemoveFile"
      @cancel="closeRemoveFile"
    />
  </div>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import ProductionSectionHead from '../../components/production/ProductionSectionHead.vue'
import MoveConfirmDialog from '../../components/MoveConfirmDialog.vue'
import { productionApi } from '../../services/productionApi'

const workspace = inject('productionWorkspace')
const production = workspace.production
const scriptBody = ref('')
const message = ref('')
const fileError = ref('')
const fileOver = ref(false)
const showRemoveModal = ref(false)
const removing = ref(false)
const removeError = ref('')

const wordCount = computed(() => scriptBody.value.trim().split(/\s+/).filter(Boolean).length)

watch(production, (value) => {
  scriptBody.value = value?.script_body || ''
}, { immediate: true })

async function saveBody () {
  await workspace.save({ script_body: scriptBody.value })
  message.value = 'Script saved.'
}

async function upload (file) {
  if (!file) return
  fileError.value = ''
  try {
    const body = await productionApi.uploadScript(workspace.productionId.value, file)
    production.value = body.data
  } catch (e) {
    fileError.value = e.response?.data?.message || 'Upload failed.'
  }
}

function onFile (event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  upload(file)
}

function onDrop (event) {
  fileOver.value = false
  upload(event.dataTransfer?.files?.[0])
}

function askRemoveFile () {
  removeError.value = ''
  showRemoveModal.value = true
}

function closeRemoveFile () {
  if (removing.value) return
  showRemoveModal.value = false
  removeError.value = ''
}

async function confirmRemoveFile () {
  if (removing.value) return
  removing.value = true
  removeError.value = ''
  try {
    const body = await productionApi.deleteScript(workspace.productionId.value)
    production.value = body.data
    showRemoveModal.value = false
  } catch (e) {
    removeError.value = e.response?.data?.message || 'Could not remove this file.'
  } finally {
    removing.value = false
  }
}
</script>
